import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Select } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';

import TagBadge from './TagBadge';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import GroupHeader from '../common/GroupHeader';
import useHome from '../../hooks/useHome';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { applyPrivateTags, removePrivateTags } from '../../services/tagApplications';
import { CLOSE } from '../icons';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { nestSubCollections, nestedOptionClass } from '../../utils/collectionFamily';
import { canTagCollectionPrivately } from '../../utils/permissions';
import { PRIVATE_TAG_COLLECTIONS_LIMIT, collectionsOfPrivateTag, privateCollectionTagRefusal, tagIdOf } from '../../utils/tags';
import { useCollections } from '../../contexts/CollectionsContext';

const byName = (a, b) => `${a?.name || ''}`.localeCompare(`${b?.name || ''}`) || (normalizeId(a?._id) < normalizeId(b?._id) ? -1 : 1);

/**
 * A PRIVATE TAG'S ⋯ → "Put on collections…" (Account › Private tags, ruled
 * b53): which collections the tag is on, and putting it on or taking it off
 * one — each change at once, through the private store (`PUT` / `DELETE
 * /api/tag-applications`, `targetType: 'collection'`). Only the person sees
 * it ("Only you see this"); on a collection it shows on the items as "From
 * the collection", for them alone.
 *
 * - WHERE IT IS NOW: Home's `collectionGroups`, the one read of the private
 *   store by collection (hooks/useHome, asked while the window is open). A
 *   change makes every Home reader read again (FavoritesContext
 *   noteHomeChanged), so Home's groups fill.
 * - WHERE IT CAN GO: the collections the person reads of their own
 *   (`GET /api/collections`, filtered by canTagCollectionPrivately — not a
 *   global collection they only follow, not one they reach only by a link:
 *   the API's `canReadCollectionMeta`).
 * - A private tag goes on 50 collections at most (`personal_tag_full`): the
 *   picker closes at 50 and says why; the API's refusals are said in plain
 *   words (utils/tags.js privateCollectionTagRefusal).
 *
 * Mounted only while open (the caller's choice).
 */
const NO_COLLECTIONS = Object.freeze([]);

export default function PrivateTagCollectionsModal({ open, onClose, tag }) {
	const tagId = tagIdOf(tag);
	const tagName = tag?.name || 'This tag';
	const { home } = useHome({ enabled: open });
	const favorites = useOptionalFavorites();
	const [on, setOn] = useState(null); // collection identities, once Home has answered
	const onRef = useRef(null);
	onRef.current = on;
	// The shared collections list, asked again when the dialog opens.
	const { collections: sharedCollections, available: collectionsLoaded, error: collectionsError } = useCollections({ refreshOnOpen: open });
	const readable = useMemo(() => ({ loading: !collectionsLoaded && !collectionsError, list: collectionsLoaded ? sharedCollections : NO_COLLECTIONS, error: collectionsLoaded ? null : collectionsError }), [collectionsLoaded, collectionsError, sharedCollections]);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);

	useEffect(() => {
		if (on !== null || !home) return;
		setOn([...collectionsOfPrivateTag(home.collectionGroups, tagId)].sort(byName));
	}, [home, on, tagId]);
	const unreadable = home === false;

	const onIds = useMemo(() => new Set((on || []).map(collection => normalizeId(collection?._id))), [on]);
	const candidates = useMemo(() => readable.list
		.filter(collection => collection && collection.status !== 'deleted' && canTagCollectionPrivately(collection))
		.filter(collection => !onIds.has(normalizeId(collection._id)))
		.sort(byName), [readable.list, onIds]);
	const options = useMemo(() => nestSubCollections(candidates).map(({ collection, depth }) => ({
		value: normalizeId(collection._id),
		title: collection.name || 'Untitled collection',
		label: collection.name || 'Untitled collection',
		className: nestedOptionClass(depth),
		collection,
	})), [candidates]);

	const change = async (collection, putOn) => {
		const cid = normalizeId(collection?._id);
		if (!cid || !tagId || busy) return;
		const before = onRef.current || [];
		setOn(putOn ? [...before, collection].sort(byName) : before.filter(entry => normalizeId(entry?._id) !== cid));
		setBusy(true);
		setRefusal(null);
		const rows = [{ tagId, targetType: 'collection', target_id: cid }];
		try {
			if (putOn) await applyPrivateTags(rows);
			else await removePrivateTags(rows);
			favorites?.noteHomeChanged?.();
		} catch (error) {
			setOn(before);
			const where = collection?.name || 'that collection';
			setRefusal(`${putOn ? `“${tagName}” was not put on ${where}.` : `“${tagName}” was not taken off ${where}.`} ${privateCollectionTagRefusal(error)}`);
		} finally {
			setBusy(false);
		}
	};

	const loading = on === null && !unreadable;
	const full = (on || []).length >= PRIVATE_TAG_COLLECTIONS_LIMIT;
	const count = (on || []).length;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Put on collections"
			destroyOnClose
			className="private-tag-collections-modal"
			footer={[<Button key="done" type="primary" onClick={onClose}>Done</Button>]}
		>
			<div className="modal-identity">
				<RecordIdentity kicker="Private tag" badge={<TagBadge tag={tag} size="md" />} />
			</div>
			<p className="meta-line collection-tags__lead">
				Only you see this. On a collection, the tag shows on its items as “From the collection”, for you alone.
			</p>
			{refusal ? <Alert type="error" showIcon closable onClose={() => setRefusal(null)} className="collection-tags__refusal" message={refusal} data-testid="private-tag-collections-refusal" /> : null}
			{unreadable ? (
				<Alert type="warning" showIcon className="collection-tags__refusal" message="Where this tag is could not be read. Close this and open it again to retry." />
			) : null}
			<DialogLoadState
				loaded={on !== null || unreadable}
				minHeight={unreadable ? 0 : 220}
				label="where this tag is"
				skeleton={<Skeleton variant="list-row" count={2} />}
			>
			{!loading && !unreadable ? (
				<>
					<GroupHeader as="h4" label="On these collections" count={count} />
					{count ? (
						<ul className="collection-tags__list" aria-label={`Collections “${tagName}” is on`}>
							{on.map(collection => {
								const cname = collection?.name || 'Untitled collection';
								return (
									<li key={normalizeId(collection._id)} className="collection-tags__row" data-testid="private-tag-collection">
										<RecordBadge kind="collection" record={collection} title={cname} size="sm" />
										<Button
											type="text"
											icon={<CLOSE />}
											aria-label={`Take “${tagName}” off ${cname}`}
											title={`Take “${tagName}” off ${cname}`}
											disabled={busy}
											onClick={() => change(collection, false)}
										/>
									</li>
								);
							})}
						</ul>
					) : <p className="meta-line collection-tags__none">It is on no collection yet. Add one below.</p>}
					<Select
						className="collection-tags__picker"
						aria-label="Add a collection"
						showSearch
						value={null}
						placeholder={readable.loading ? 'Loading your collections…' : 'Add a collection'}
						options={options}
						optionRender={option => (
							<RecordBadge kind="collection" record={option.data.collection} title={option.data.title} size="xs" />
						)}
						filterOption={(input, option) => `${option?.title || ''}`.toLowerCase().includes(`${input || ''}`.toLowerCase())}
						onChange={value => {
							const picked = candidates.find(collection => normalizeId(collection._id) === value);
							if (picked) change(picked, true);
						}}
						disabled={busy || full || readable.loading}
						title={full ? `A private tag goes on ${PRIVATE_TAG_COLLECTIONS_LIMIT} collections at most.` : undefined}
						notFoundContent={readable.error ? 'Your collections could not be loaded.' : 'No other collection you can read'}
					/>
					<p className="meta-line collection-tags__cap">
						{full
							? `It is on ${PRIVATE_TAG_COLLECTIONS_LIMIT} collections, the most a private tag goes on. Take it off one to add another.`
							: `A private tag goes on ${PRIVATE_TAG_COLLECTIONS_LIMIT} collections at most${count ? `; this one is on ${formatCount(count, 'collection')}` : ''}.`}
					</p>
				</>
			) : null}
			</DialogLoadState>
		</Modal>
	);
}
